import { forwardRef, type AnchorHTMLAttributes, type MouseEvent } from "react";
import type { CampaignKey } from "@/campaigns/campaign-key";
import { useCampaignNavigation } from "@/contexts/campaign-navigation";
import { campaignRoute } from "@/routes";

interface CampaignLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  campaignId: CampaignKey;
}

/**
 * A link to one campaign's page that works on every surface: it navigates
 * through `CampaignNavigationContext` rather than react-router's `Link`, so the
 * shared campaign cards don't drag a router into the overlay bundle. Same
 * markup and click rules as `Link` — a modified or already-handled click is
 * left to the browser.
 */
export const CampaignLink = forwardRef<HTMLAnchorElement, CampaignLinkProps>(
  ({ campaignId, onClick, ...props }, ref) => {
    const navigateToCampaign = useCampaignNavigation();

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.altKey ||
        event.ctrlKey ||
        event.shiftKey
      ) {
        return;
      }
      event.preventDefault();
      navigateToCampaign(campaignId);
    };

    return (
      <a {...props} ref={ref} href={campaignRoute} onClick={handleClick} />
    );
  }
);
CampaignLink.displayName = "CampaignLink";
